import React, { useState } from 'react';
import { UserPlus, Mail, Shield, Edit, Trash2, Clock, CheckCircle, XCircle, Search } from 'lucide-react';
import Card from '../ui/CardComponent';
import Button from '../ui/ButtonComponent';
import Input from '../ui/InputComponent';
import Badge from '../ui/BadgeComponent';
import Modal from '../ui/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export default function Team() {
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const teamMembers = [
    {
      id: 1,
      name: 'Sarah Johnson',
      email: 'sarah.johnson@company.com',
      role: 'Admin',
      status: 'active',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100',
      joinedDate: '2024-01-15',
      lastActive: '2 hours ago',
    },
    {
      id: 2,
      name: 'Michael Chen',
      email: 'michael.chen@company.com',
      role: 'Editor',
      status: 'active',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100',
      joinedDate: '2024-03-20',
      lastActive: '1 day ago',
    },
    {
      id: 3,
      name: 'Emily Davis',
      email: 'emily.davis@company.com',
      role: 'Editor',
      status: 'active',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100',
      joinedDate: '2024-05-10',
      lastActive: '3 hours ago',
    },
    {
      id: 4,
      name: 'James Wilson',
      email: 'james.wilson@company.com',
      role: 'Viewer',
      status: 'active',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100',
      joinedDate: '2024-07-22',
      lastActive: '5 days ago',
    },
    {
      id: 5,
      name: 'Lisa Anderson',
      email: 'lisa.anderson@company.com',
      role: 'Editor',
      status: 'pending',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100',
      joinedDate: '2024-11-25',
      lastActive: 'Never',
    },
  ];

  const permissions = [
    { id: 'create_posts', label: 'Create Posts', admin: true, editor: true, viewer: false },
    { id: 'edit_posts', label: 'Edit Posts', admin: true, editor: true, viewer: false },
    { id: 'delete_posts', label: 'Delete Posts', admin: true, editor: false, viewer: false },
    { id: 'schedule_posts', label: 'Schedule Posts', admin: true, editor: true, viewer: false },
    { id: 'view_analytics', label: 'View Analytics', admin: true, editor: true, viewer: true },
    { id: 'manage_media', label: 'Manage Media Library', admin: true, editor: true, viewer: false },
    { id: 'manage_team', label: 'Manage Team', admin: true, editor: false, viewer: false },
    { id: 'manage_settings', label: 'Manage Settings', admin: true, editor: false, viewer: false },
  ];

  const activityLog = [
    {
      id: 1,
      user: 'Sarah Johnson',
      action: 'Created a new post',
      details: 'Instagram - Product Launch',
      timestamp: '2 hours ago',
      type: 'create',
    },
    {
      id: 2,
      user: 'Michael Chen',
      action: 'Edited a post',
      details: 'Facebook - Behind the Scenes',
      timestamp: '5 hours ago',
      type: 'edit',
    },
    {
      id: 3,
      user: 'Emily Davis',
      action: 'Scheduled a post',
      details: 'LinkedIn - Industry Insights',
      timestamp: '1 day ago',
      type: 'schedule',
    },
    {
      id: 4,
      user: 'Sarah Johnson',
      action: 'Invited team member',
      details: 'lisa.anderson@company.com',
      timestamp: '2 days ago',
      type: 'invite',
    },
  ];

  const filteredMembers = teamMembers.filter(
    (member) =>
      member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'Admin':
        return 'danger';
      case 'Editor':
        return 'info';
      case 'Viewer':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'inactive':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <div className="min-h-screen md:ml-20 lg:ml-64 pt-16 md:pt-0 pb-20 md:pb-0">
      <div className="p-4 md:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-gray-900 dark:text-white mb-2">Team & Roles</h1>
            <p className="text-gray-600 dark:text-gray-400">Manage your team members and their permissions</p>
          </div>
          <Button variant="primary" onClick={() => setInviteModalOpen(true)}>
            <UserPlus size={16} />
            <span>Invite Member</span>
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <Card>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-violet-50 dark:bg-violet-900/20 rounded-xl">
                <Shield className="text-violet-600 dark:text-violet-400" size={24} />
              </div>
              <div>
                <div className="text-2xl text-gray-900 dark:text-white">
                  {teamMembers.filter((m) => m.status === 'active').length}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Active Members</div>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
                <Clock className="text-amber-600 dark:text-amber-400" size={24} />
              </div>
              <div>
                <div className="text-2xl text-gray-900 dark:text-white">
                  {teamMembers.filter((m) => m.status === 'pending').length}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Pending Invites</div>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                <Mail className="text-blue-600 dark:text-blue-400" size={24} />
              </div>
              <div>
                <div className="text-2xl text-gray-900 dark:text-white">{teamMembers.length}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Total Users</div>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Team Members */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <div className="mb-6">
                <Input
                  placeholder="Search team members..."
                  icon={<Search size={16} />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="space-y-3">
                {filteredMembers.map((member) => (
                  <div
                    key={member.id}
                    className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <img
                      src={member.avatar}
                      alt={member.name}
                      className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-gray-900 dark:text-white">{member.name}</h3>
                        <Badge variant={getRoleBadgeVariant(member.role)} size="sm">
                          {member.role}
                        </Badge>
                        <Badge variant={getStatusBadgeVariant(member.status)} size="sm">
                          {member.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{member.email}</p>
                      <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
                        <span>Joined {member.joinedDate}</span>
                        <span>•</span>
                        <span>Last active {member.lastActive}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedMember(member);
                          setPermissionsModalOpen(true);
                        }}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Permissions Matrix - Desktop */}
            <Card className="hidden lg:block">
              <h3 className="text-gray-900 dark:text-white mb-6">Permissions Matrix</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 text-sm text-gray-600 dark:text-gray-400">Permission</th>
                      <th className="text-center py-3 px-4 text-sm text-gray-600 dark:text-gray-400">Admin</th>
                      <th className="text-center py-3 px-4 text-sm text-gray-600 dark:text-gray-400">Editor</th>
                      <th className="text-center py-3 px-4 text-sm text-gray-600 dark:text-gray-400">Viewer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {permissions.map((permission) => (
                      <tr key={permission.id} className="border-b border-gray-200 dark:border-gray-700 last:border-0">
                        <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">{permission.label}</td>
                        <td className="text-center py-3 px-4">
                          {permission.admin ? (
                            <CheckCircle className="inline text-green-600 dark:text-green-400" size={18} />
                          ) : (
                            <XCircle className="inline text-gray-300 dark:text-gray-600" size={18} />
                          )}
                        </td>
                        <td className="text-center py-3 px-4">
                          {permission.editor ? (
                            <CheckCircle className="inline text-green-600 dark:text-green-400" size={18} />
                          ) : (
                            <XCircle className="inline text-gray-300 dark:text-gray-600" size={18} />
                          )}
                        </td>
                        <td className="text-center py-3 px-4">
                          {permission.viewer ? (
                            <CheckCircle className="inline text-green-600 dark:text-green-400" size={18} />
                          ) : (
                            <XCircle className="inline text-gray-300 dark:text-gray-600" size={18} />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Permissions - Mobile Accordion */}
            <Card className="lg:hidden">
              <h3 className="text-gray-900 dark:text-white mb-4">Permissions</h3>
              <div className="space-y-4">
                {['Admin', 'Editor', 'Viewer'].map((role) => (
                  <div key={role} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                    <h4 className="text-gray-900 dark:text-white mb-3">{role}</h4>
                    <div className="space-y-2">
                      {permissions
                        .filter((p) => p[role.toLowerCase() as keyof typeof p])
                        .map((permission) => (
                          <div key={permission.id} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="text-green-600 dark:text-green-400" size={16} />
                            <span className="text-gray-600 dark:text-gray-400">{permission.label}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Activity Log */}
          <Card>
            <h3 className="text-gray-900 dark:text-white mb-6">Activity Log</h3>
            <div className="space-y-4">
              {activityLog.map((activity) => (
                <div
                  key={activity.id}
                  className="relative pl-6 pb-4 border-l-2 border-gray-200 dark:border-gray-700 last:border-0 last:pb-0"
                >
                  <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-violet-600 dark:bg-violet-400" />
                  <div>
                    <p className="text-sm text-gray-900 dark:text-white mb-1">
                      <span>{activity.user}</span>{' '}
                      <span className="text-gray-600 dark:text-gray-400">{activity.action}</span>
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{activity.details}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{activity.timestamp}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Invite Modal */}
        <Modal
          isOpen={inviteModalOpen}
          onClose={() => setInviteModalOpen(false)}
          title="Invite Team Member"
          footer={
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => setInviteModalOpen(false)} fullWidth>
                Cancel
              </Button>
              <Button variant="primary" fullWidth>
                Send Invite
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <Input label="Email Address" type="email" placeholder="colleague@company.com" />
            <label>Role</label>
            <Select>
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="viewer">Viewer - Can view analytics only</SelectItem>
                <SelectItem value="editor">Editor - Can create and edit posts</SelectItem>
                <SelectItem value="admin">Admin - Full access</SelectItem>
              </SelectContent>
            </Select>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <p className="text-sm text-blue-900 dark:text-blue-200">
                An invitation email will be sent to the provided address with instructions to join your team.
              </p>
            </div>
          </div>
        </Modal>

        {/* Edit Permissions Modal */}
        <Modal
          isOpen={permissionsModalOpen}
          onClose={() => {
            setPermissionsModalOpen(false);
            setSelectedMember(null);
          }}
          title="Edit Member Permissions"
          footer={
            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setPermissionsModalOpen(false);
                  setSelectedMember(null);
                }}
                fullWidth
              >
                Cancel
              </Button>
              <Button variant="primary" fullWidth>
                Save Changes
              </Button>
            </div>
          }
        >
          {selectedMember && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                <img
                  src={selectedMember.avatar}
                  alt={selectedMember.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <h3 className="text-gray-900 dark:text-white">{selectedMember.name}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{selectedMember.email}</p>
                </div>
              </div>
              <label>Role</label>
              <Select defaultValue={selectedMember.role.toLowerCase()}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Viewer</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
}
